import { useCallback, useEffect, useState } from "react";
import { RefreshCcw, Mail, AlertTriangle } from "lucide-react";
import { api } from "../../api";
import PageHeader from "../../components/PageHeader";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { toast } from "sonner";

const STATUS_META = {
  queued: { bg: "bg-amber-50", text: "text-amber-800", label: "Queued" },
  sent: { bg: "bg-[#E9F5EC]", text: "text-[#1B4332]", label: "Sent" },
  failed: { bg: "bg-[#FDF3F3]", text: "text-[#C94A4A]", label: "Failed" },
};

export default function Outbox() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("__all__");
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (status !== "__all__") params.set("status", status);
    try { const { data } = await api.get(`/admin/outbox?${params}`); setItems(data); }
    finally { setLoading(false); }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const resend = async (id) => {
    try { const { data } = await api.post(`/admin/outbox/${id}/resend`); setItems((p) => [data, ...p]); toast.success("Resend queued as new outbox entry"); }
    catch { toast.error("Failed"); }
  };

  const failedCount = items.filter((e) => e.status === "failed").length;

  return (
    <>
      <PageHeader title="Email outbox" subtitle="Every rendered email — sent, queued, or failed. Click a row to view the body." testid="page-outbox"
        actions={
          <div className="flex items-center gap-2">
            {status !== "failed" && failedCount > 0 && (
              <Button
                variant="outline"
                className="h-9 border-[#f2c7c7] text-[#C94A4A] hover:bg-[#FDF3F3]"
                onClick={() => setStatus("failed")}
                data-testid="outbox-quick-failed"
              >
                <AlertTriangle size={14} /> {failedCount} failed — show
              </Button>
            )}
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="h-9 w-44" data-testid="outbox-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All delivery statuses</SelectItem>
                <SelectItem value="queued">Queued only</SelectItem>
                <SelectItem value="sent">Sent only</SelectItem>
                <SelectItem value="failed">Failed only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        }
      />
      <div className="p-6">
        <div className="bg-white border border-[#E2E4E0] rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-[#F9F9F8] text-[#5C665A]">
              <tr>
                <th className="text-left px-3 py-2 db-h font-semibold">When</th>
                <th className="text-left px-3 py-2 db-h font-semibold">Recipient</th>
                <th className="text-left px-3 py-2 db-h font-semibold">Template · Subject</th>
                <th className="text-center px-3 py-2 db-h font-semibold w-32">Delivery status</th>
                <th className="px-3 py-2 w-24"></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={5} className="px-3 py-8 text-center text-[#5C665A]" data-testid="outbox-loading">Loading…</td></tr>}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-8 text-center text-[#5C665A]" data-testid="outbox-empty">
                    <div className="font-medium text-[#1A1D1A]">No emails yet.</div>
                    <div className="text-xs mt-1">
                      Emails are generated here as audit records. They are only
                      actually delivered once <b>Mailgun</b> is configured in Settings.
                    </div>
                  </td>
                </tr>
              )}
              {items.map((e) => {
                const s = STATUS_META[e.status] || STATUS_META.queued;
                const isFailed = e.status === "failed";
                const rowCls = isFailed
                  ? "border-t border-[#F2C7C7] bg-[#FDF3F3] db-row cursor-pointer"
                  : "border-t border-[#E2E4E0] db-row cursor-pointer";
                return (
                  <tr key={e.id} className={rowCls} onClick={() => setPreview(e)} data-testid={`outbox-row-${e.id}`}>
                    <td className="px-3 py-2 text-xs text-[#5C665A] db-mono">{new Date(e.created_at).toLocaleString()}</td>
                    <td className="px-3 py-2">{e.to}</td>
                    <td className="px-3 py-2">
                      <div className="text-xs text-[#5C665A] db-mono">{e.template_key}</div>
                      <div className="font-medium truncate max-w-md">{e.subject}</div>
                      {isFailed && e.error && (
                        <div
                          className="text-[11px] text-[#C94A4A] mt-0.5 inline-flex items-start gap-1"
                          title={e.error}
                          data-testid={`outbox-error-${e.id}`}
                        >
                          <AlertTriangle size={11} className="mt-0.5 shrink-0" />
                          <span className="truncate max-w-md">{e.error}</span>
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <Badge
                        className={`${s.bg} ${s.text} border-transparent inline-flex items-center gap-1`}
                        data-testid={`outbox-status-${e.id}`}
                      >
                        {isFailed && <AlertTriangle size={10} />}
                        {s.label}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-right" onClick={(evt) => evt.stopPropagation()}>
                      <button className="text-[#2D6A4F] hover:bg-[#E9F5EC] inline-flex items-center gap-1 h-7 px-2 rounded-md text-xs" onClick={() => resend(e.id)} data-testid={`outbox-resend-${e.id}`}>
                        <RefreshCcw size={12} /> Resend
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      </div>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-2xl" data-testid="outbox-preview">
          <DialogHeader>
            <DialogTitle className="db-h flex items-center gap-2"><Mail size={16} /> {preview?.subject}</DialogTitle>
            <DialogDescription>
              To <span className="db-mono">{preview?.to}</span> · Template <span className="db-mono">{preview?.template_key}</span>
              {preview?.error && (
                <span className="block mt-2 text-[#C94A4A] bg-[#FDF3F3] border border-[#f2c7c7] rounded-md p-2 text-xs" data-testid="outbox-preview-error">
                  <AlertTriangle size={11} className="inline mr-1" />
                  <b>Delivery error:</b> {preview.error}
                </span>
              )}
              {preview?.warning && !preview?.error && (
                <span className="block mt-2 text-[#8A5A2B] bg-[#FBF3E8] border border-[#D4A373] rounded-md p-2 text-xs" data-testid="outbox-preview-warning">
                  <AlertTriangle size={11} className="inline mr-1" />
                  {preview.warning}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <pre className="whitespace-pre-wrap text-sm bg-[#F9F9F8] rounded-md border border-[#E2E4E0] p-3 max-h-96 overflow-auto db-mono" data-testid="outbox-preview-body">{preview?.body}</pre>
        </DialogContent>
      </Dialog>
    </>
  );
}
